import React, { useEffect } from 'react';
import {
    Sidebar, SidebarNav, SidebarLayout,
    Container, RowView, Col, Heading,
    Button, CardView, Badge, Alert,
    useResponsive, useCapacitor, IOSOnly, AndroidOnly, WebOnly,
    initializeCapacitor, getPlatformClass
} from './index';

/**
 * Capacitor-Ready App Component
 * Handles iOS, Android, and Web differences automatically
 */
const CapacitorApp = () => {
    const screen = useResponsive();
    const platform = useCapacitor();

    useEffect(() => {
        // Initialize Capacitor on mount
        const init = async () => {
            await initializeCapacitor({
                statusBarStyle: 'Light',
                statusBarColor: '#2c3e50',
                enableSafeArea: true
            });
        };

        init();
    }, []);

    const navItems = [
        { icon: '🏠', label: 'Home', key: 'home' },
        { icon: '📊', label: 'Analytics', key: 'analytics' },
        { icon: '⚙️', label: 'Settings', key: 'settings' }
    ];

    return (
        <div className={getPlatformClass()}>
            <SidebarLayout
                sidebar={
                    <Sidebar
                        bg="#2c3e50"
                        fixed={true}
                        collapsible={true}
                        useMobileOffcanvas={screen.isMobile || platform.isNative}
                        className="safe-area-container"
                    >
                        <Heading level={3} style={{ color: 'white', textAlign: 'center' }}>
                            MyApp
                        </Heading>

                        {/* Platform Info */}
                        <Alert variant="info" style={{ fontSize: '12px', padding: '8px' }}>
                            Platform: {platform.platform}
                            <br />
                            Device: {screen.isMobile ? 'Mobile' : screen.isTablet ? 'Tablet' : 'Desktop'}
                        </Alert>

                        <SidebarNav items={navItems} />
                    </Sidebar>
                }
            >
                <Container fluid className="safe-area-top">
                    {/* Platform-specific welcome message */}
                    <IOSOnly>
                        <Alert variant="primary">
                            🍎 Welcome iOS User!
                        </Alert>
                    </IOSOnly>

                    <AndroidOnly>
                        <Alert variant="success">
                            🤖 Welcome Android User!
                        </Alert>
                    </AndroidOnly>

                    <WebOnly>
                        <Alert variant="info">
                            🌐 Welcome Web User!
                        </Alert>
                    </WebOnly>

                    <Heading level={1}>Dashboard</Heading>

                    {/* Stats Cards */}
                    <RowView xs={1} sm={2} lg={4}>
                        <Col>
                            <CardView bg="primary" text="white">
                                <Heading level={2} style={{ color: 'white' }}>
                                    {platform.isIOS ? '🍎' : platform.isAndroid ? '🤖' : '🌐'}
                                </Heading>
                                <p>Platform: {platform.platform}</p>
                            </CardView>
                        </Col>

                        <Col>
                            <CardView bg="success" text="white">
                                <Heading level={2} style={{ color: 'white' }}>
                                    {screen.width}px
                                </Heading>
                                <p>Screen Width</p>
                            </CardView>
                        </Col>

                        <Col>
                            <CardView bg="warning">
                                <Heading level={2}>
                                    {screen.isMobile ? '📱' : screen.isTablet ? '📱' : '💻'}
                                </Heading>
                                <p>
                                    {screen.isMobile ? 'Mobile' : screen.isTablet ? 'Tablet' : 'Desktop'}
                                </p>
                            </CardView>
                        </Col>

                        <Col>
                            <CardView bg="info" text="white">
                                <Heading level={2} style={{ color: 'white' }}>
                                    {platform.isNative ? '📱' : '🌐'}
                                </Heading>
                                <p>{platform.isNative ? 'Native App' : 'Web Browser'}</p>
                            </CardView>
                        </Col>
                    </RowView>

                    {/* Platform-specific features */}
                    {platform.deviceInfo && (
                        <CardView title="Device Information">
                            <p><strong>OS:</strong> {platform.deviceInfo.operatingSystem}</p>
                            <p><strong>Version:</strong> {platform.deviceInfo.osVersion}</p>
                            <p><strong>Model:</strong> {platform.deviceInfo.model}</p>
                            <p><strong>Manufacturer:</strong> {platform.deviceInfo.manufacturer}</p>
                        </CardView>
                    )}

                    {/* Safe Area Information */}
                    {platform.isNative && (
                        <CardView title="Safe Area Insets">
                            <RowView xs={2} md={4}>
                                <Col>
                                    <Badge bg="primary">Top: {platform.safeAreaInsets.top}px</Badge>
                                </Col>
                                <Col>
                                    <Badge bg="success">Bottom: {platform.safeAreaInsets.bottom}px</Badge>
                                </Col>
                                <Col>
                                    <Badge bg="warning">Left: {platform.safeAreaInsets.left}px</Badge>
                                </Col>
                                <Col>
                                    <Badge bg="info">Right: {platform.safeAreaInsets.right}px</Badge>
                                </Col>
                            </RowView>
                        </CardView>
                    )}

                    {/* Platform-specific buttons */}
                    <CardView title="Platform-Specific Actions">
                        <RowView xs={1} md={3}>
                            <Col>
                                <IOSOnly>
                                    <Button variant="primary" block>
                                        🍎 iOS Feature
                                    </Button>
                                </IOSOnly>
                            </Col>
                            <Col>
                                <AndroidOnly>
                                    <Button variant="success" block>
                                        🤖 Android Feature
                                    </Button>
                                </AndroidOnly>
                            </Col>
                            <Col>
                                <WebOnly>
                                    <Button variant="info" block>
                                        🌐 Web Feature
                                    </Button>
                                </WebOnly>
                            </Col>
                        </RowView>
                    </CardView>
                </Container>
            </SidebarLayout>
        </div>
    );
};

export default CapacitorApp;

